import { State, Action, StateContext, Selector, NgxsOnInit, Store } from '@ngxs/store';
import { SubscriptionStateModel } from './subscription.state.model';
import { SubscriptionService } from '../../subscription.service';
import {
  BuySubscription,
  BuySubscriptionFailure,
  BuySubscriptionSuccess,
  CancelSubscription,
  CancelSubscriptionFailure,
  CancelSubscriptionSuccess,
  CheckPromoCode,
  CheckPromoCodeFailure,
  CheckPromoCodeSuccess,
  GetPremiumPlans,
  GetPremiumPlansFailure,
  GetPremiumPlansSuccess,
  RemovePromoCode,
  ResetSubscriptionState,
  SetPremiumPlan,
  UpdateSubscription,
  UpdateSubscriptionFailure,
  UpdateSubscriptionSuccess,
} from './subscription.actions';
import { filter, finalize, flatMap, tap } from 'rxjs/operators';
import { AuthService } from '../../auth.service';
import { environment } from 'src/environments/environment';
import { EnvironmentDeploy } from 'src/environments/models/environment-deploy';
import { VerifyFail } from '../auth/auth.actions';
import { Injectable } from '@angular/core';
import { get, isNil } from 'lodash';
import { AuthState } from '../auth/auth.state';
import { AuthStateModel } from '../auth/auth.state.model';
import { PendoService } from 'src/app/shared/pendo.service';
import { HumanVerificationModel } from '../../models/verification/human-verification.model';

const initialState: SubscriptionStateModel = {
  buying: false,
  updating: false,
  cancelling: false,
  checkingCode: false,
  plans: [],
  selectedPlan: undefined,
};

@State<SubscriptionStateModel>({
  name: 'subscription',
  defaults: initialState,
})
@Injectable()
export class SubscriptionState implements NgxsOnInit {
  @Selector()
  static getBuying(state: SubscriptionStateModel) {
    return state.buying;
  }

  @Selector()
  static getUpdating(state: SubscriptionStateModel) {
    return state.updating;
  }

  @Selector()
  static getCancelling(state: SubscriptionStateModel) {
    return state.cancelling;
  }

  @Selector([AuthState])
  static getSubscription(state: SubscriptionStateModel, profileState: AuthStateModel) {
    const subscription = get(profileState, 'userProfile.subscription');
    return subscription;
  }

  @Selector()
  static getPlans(state: SubscriptionStateModel) {
    return state.plans;
  }

  @Selector()
  static getSelectedPlan(state: SubscriptionStateModel) {
    return state.selectedPlan;
  }

  @Selector()
  static getCheckingCode(state: SubscriptionStateModel) {
    return state.checkingCode;
  }

  @Selector([AuthState])
  static isFreeTrial(state: SubscriptionStateModel, profileState: AuthStateModel) {
    const subscription_type = get(profileState, 'userProfile.subscription.subscription_type');
    return subscription_type === 'Free Trial';
  }

  @Selector([AuthState])
  static isCanceled(state: SubscriptionStateModel, profileState: AuthStateModel) {
    const subscription_status = get(profileState, 'userProfile.subscription.subscription_status');
    return subscription_status === 'canceled';
  }

  /**
   * Actions
   */
  @Action(ResetSubscriptionState)
  resetSubscriptionState(
    context: StateContext<SubscriptionStateModel>,
    action: ResetSubscriptionState,
  ) {
    context.setState(initialState);
  }

  @Action(GetPremiumPlans)
  getPremiumPlans(context: StateContext<SubscriptionStateModel>, action: GetPremiumPlans) {
    this.subscriptionService.getBraintreePlans().subscribe(
      (plans) => {
        context.dispatch(new GetPremiumPlansSuccess(plans));
      },
      (err) => {
        context.dispatch(new GetPremiumPlansFailure(err));
      },
    );
  }

  @Action(GetPremiumPlansSuccess)
  getPremiumPlansSuccess(
    context: StateContext<SubscriptionStateModel>,
    { plans }: GetPremiumPlansSuccess,
  ) {
    context.patchState({
      plans,
    });
  }

  @Action(SetPremiumPlan)
  setPremiumPlan(context: StateContext<SubscriptionStateModel>, { plan }: SetPremiumPlan) {
    context.patchState({
      selectedPlan: plan,
    });
  }

  @Action(BuySubscription)
  buySubscription(context: StateContext<SubscriptionStateModel>, { request }: BuySubscription) {
    context.patchState({ buying: true });

    // Disable Recaptcha when working locally
    if (environment.deploy === EnvironmentDeploy.Local) {
      this.subscriptionService
        .buyProfessionalSubscription(request)
        .pipe(finalize(() => context.patchState({ buying: false })))
        .subscribe(
          () => {
            context.dispatch(new BuySubscriptionSuccess(request));
          },
          (err) => {
            context.dispatch(new BuySubscriptionFailure(err));
          },
        );
      // Production Login
    } else {
      this.authService
        .isHuman('subscription_checkout')
        .pipe(
          tap((response: HumanVerificationModel) => {
            if (!response.isHuman) {
              context.dispatch(new VerifyFail());
            }
          }),
          filter((response: HumanVerificationModel) => response.isHuman),
          flatMap(() => this.subscriptionService.buyProfessionalSubscription(request)),
          finalize(() => context.patchState({ buying: false })),
        )
        .subscribe(
          () => {
            context.dispatch(new BuySubscriptionSuccess(request));
          },
          (err) => {
            context.dispatch(new BuySubscriptionFailure(err));
          },
        );
    }
  }

  @Action(BuySubscriptionSuccess)
  buySubscriptionSuccess(
    context: StateContext<SubscriptionStateModel>,
    { request }: BuySubscriptionSuccess,
  ) {
    const freeTrial = this.store.selectSnapshot(SubscriptionState.isFreeTrial);
    const eventName = freeTrial
      ? 'Free Trial to Premium Individual'
      : 'Free Individual to Premium Individual';

    this.pendoService.track(`Account Upgrade - ${eventName}`);
  }

  @Action(UpdateSubscription)
  updateSubscription(
    context: StateContext<SubscriptionStateModel>,
    { request }: UpdateSubscription,
  ) {
    context.patchState({ updating: true });

    this.subscriptionService
      .updateProfessionalSubscription(request)
      .pipe(finalize(() => context.patchState({ updating: false })))
      .subscribe(
        () => {
          context.dispatch(new UpdateSubscriptionSuccess());
        },
        (err) => {
          context.dispatch(new UpdateSubscriptionFailure(err));
        },
      );
  }

  @Action(CancelSubscription)
  cancelSubscription(context: StateContext<SubscriptionStateModel>, action: CancelSubscription) {
    context.patchState({ cancelling: true });

    this.subscriptionService
      .cancelSubscription()
      .pipe(finalize(() => context.patchState({ cancelling: false })))
      .subscribe(
        () => {
          context.dispatch(new CancelSubscriptionSuccess());
        },
        (err) => {
          context.dispatch(new CancelSubscriptionFailure(err));
        },
      );
  }

  @Action(CheckPromoCode)
  checkPromoCode(context: StateContext<SubscriptionStateModel>, { planId, code }: CheckPromoCode) {
    context.patchState({ checkingCode: true });

    this.subscriptionService
      .getBraintreePlan(planId, code)
      .pipe(finalize(() => context.patchState({ checkingCode: false })))
      .subscribe(
        (plan) => {
          context.dispatch(new CheckPromoCodeSuccess(code, plan));
        },
        (err) => {
          context.dispatch(new CheckPromoCodeFailure(err));
        },
      );
  }

  @Action(CheckPromoCodeSuccess)
  checkPromoCodeSuccess(
    context: StateContext<SubscriptionStateModel>,
    { plan }: CheckPromoCodeSuccess,
  ) {
    // patch selected plan
    context.patchState({
      selectedPlan: plan,
    });
  }

  @Action(RemovePromoCode)
  removePromoCode(context: StateContext<SubscriptionStateModel>, action: RemovePromoCode) {
    // remove discount + revert to original plan
    const { plans, selectedPlan } = context.getState();
    if (!isNil(selectedPlan)) {
      const selectedPlanId = selectedPlan.plan_id;
      context.patchState({
        selectedPlan: plans.find((plan) => plan.plan_id === selectedPlanId),
      });
    }
  }

  constructor(
    private authService: AuthService,
    private subscriptionService: SubscriptionService,
    private pendoService: PendoService,
    private store: Store,
  ) {}

  ngxsOnInit(context: StateContext<SubscriptionStateModel>) {}
}
